import { bus } from "@/main.js";
import { mapActions } from "vuex";
import { checkPermissionsMixin } from "./checkPermissionsMixin";

export const homeReportPageMixin = {
  mixins: [checkPermissionsMixin],

  data() {
    return {
      selectedWorkspace: undefined,
      workspaceOptions: [],
      workspaceSelectionDisabled: false,
      workspaceTechnicalSitesWithLocals: [],
      networkStartingPointOptions: null,
      closureOptions: [],
      buildingOptions: [],
      reportParameters: []
    };
  },

  computed: {
    hasPermissionToCreateExport() {
      return this.userHasManagementPermission("export_store");
    },

    currentWorkspaceUuid() {
      let currentWorkspaceUuid = this.$store.getters.workspace;
      return currentWorkspaceUuid.uuid;
    }
  },

  watch: {
    currentWorkspaceUuid() {
      return this.setWorkspaceField();
    }
  },

  mounted() {
    bus.$on("add-home-report-file-to-list", () =>
      this.resetReportParameterValues(this.reportParameters, 0)
    );
  },

  destroyed() {
    bus.$off("add-home-report-file-to-list");
  },

  methods: {
    ...mapActions([
      "fetchAllWorkspaces",
      "GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS"
    ]),

    /////////////////////////////
    /*Workspace related methods*/
    /////////////////////////////
    /**
     * Sets the values necessary for a Workspace field.
     */
    setWorkspaceField() {
      return new Promise(resolve => {
        this.getWorkspaceOptions().then(workspaces => {
          this.workspaceOptions = this.optionListNormalizer(workspaces, "wp");

          if (this.workspaceIsPreSelected(this.currentWorkspaceUuid)) {
            this.selectedWorkspace = this.currentWorkspaceUuid;
            let workspaceGeom = this.workspaceOptions.filter(
              item => item.id === this.selectedWorkspace
            );
            this.selectedWorkspaceGeom = workspaceGeom[0].geom;

            if (!this.isAdmin) {
              this.workspaceSelectionDisabled = true;
            }
          }
        });

        resolve();
      });
    },

    /**
     * Creates a list of workspace options considering if the user as a single workspace open or more than one.
     */
    getWorkspaceOptions() {
      return new Promise((resolve, reject) => {
        this.fetchAllWorkspaces({
          includes: undefined,
          fields: "wp_uuid,wp_label,wp_id,geom"
        })
          .then(workspaces => {
            resolve(workspaces);
          })
          .catch(error => {
            console.error(error);
            reject();
          });
      });
    },

    /**
     * Checks if the user selected a Workspace on entering the Fibercloud Home page.
     * @param {Object} currentWorkspaceUuid - workspace uuid opened by the user
     * @returns {boolean}
     */
    workspaceIsPreSelected(currentWorkspaceUuid) {
      return currentWorkspaceUuid !== "";
    },

    //////////////////////////////////////
    /*Technical Site With Locals methods*/
    //////////////////////////////////////

    /**
     * Gets a list of all the technical sites (with respective technical locals) in the selected workspace.
     */
    getWorkspaceTechnicalSitesWithLocals() {
      return new Promise((resolve, reject) => {
        this.GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS(this.selectedWorkspace)
          .then(workspaceTechnicalSitesWithLocals => {
            this.workspaceTechnicalSitesWithLocals = workspaceTechnicalSitesWithLocals;
            resolve(workspaceTechnicalSitesWithLocals);
          })
          .catch(error => {
            console.log(error);
            reject(error);
          });
      });
    },

    /**
     * Creates a list with all the Central Offices options with respective Technical Locals inside of the selected
     * workspace.
     */
    getNetworkStartingPointOptions({ callback }) {
      if (this.selectedWorkspace) {
        this.getWorkspaceTechnicalSitesWithLocals().then(
          technicalSitesWithLocals => {
            this.networkStartingPointOptions = this.getTechnicalSiteOfTypeWithTechnicalLocalOptions(
              technicalSitesWithLocals
            );
            callback();
          }
        );
      } else {
        callback();
      }
    },

    /**
     * Creates a list with all the Central Offices options with respective Technical Locals inside of the selected
     * workspace.
     */
    getWorkspaceCentralOfficesOptions(technicalSitesWithLocals) {
      return this.getTechnicalSiteOfTypeWithTechnicalLocalOptions(
        technicalSitesWithLocals,
        "OLT"
      );
    },

    /**
     * Creates a list with all the Primary Nodes options with respective Technical Locals inside of the selected
     * workspace.
     */
    getWorkspacePrimaryNodesOptions(technicalSitesWithLocals) {
      return this.getTechnicalSiteOfTypeWithTechnicalLocalOptions(
        technicalSitesWithLocals,
        "FDC"
      );
    },

    /**
     * Creates a list with all the Technical Sites options with respective Technical Locals, for a specific logical
     * type, inside of the selected workspace.
     * @param {Array} technicalSitesWithLocals - list of technical sites with technical locals
     * @param {String} logicalType - logical type of the technical site/local
     * @return {Array}
     */
    getTechnicalSiteOfTypeWithTechnicalLocalOptions(
      technicalSitesWithLocals,
      logicalType = undefined
    ) {
      let technicalSiteOptions = [];
      if (technicalSitesWithLocals && technicalSitesWithLocals.length > 0) {
        technicalSitesWithLocals.forEach(technicalSite => {
          if (technicalSite.technicallocals.length > 0) {
            let technicalSiteType = technicalSite.ts_logical_type;
            if (
              logicalType === undefined ||
              logicalType === technicalSiteType
            ) {
              let technicalSiteOption = this.createTechnicalSiteOption(
                technicalSite,
                technicalSiteType
              );
              technicalSiteOptions.push(technicalSiteOption);
            }
          }
        });
      }
      return technicalSiteOptions;
    },

    /**
     * Creates a Technical site object that the treeselect component can read as an option.
     * @param {object} technicalSite - Original Technical site object
     * @param {String} technicalSiteType - type of the technical site
     * @return {{ id: string, label: string, children: []}} the adapted object
     */
    createTechnicalSiteOption(technicalSite, technicalSiteType) {
      let technicalSiteTypeLabel = "";
      if (technicalSiteType === "OLT") {
        technicalSiteTypeLabel = "CO";
      } else if (technicalSiteType === "FDC") {
        technicalSiteTypeLabel = "PN";
      }
      let technicalSiteLabel =
        technicalSiteTypeLabel + " " + technicalSite.ts_id;
      if (technicalSite.ts_label && technicalSite.ts_label !== "") {
        technicalSiteLabel += ": " + technicalSite.ts_label;
      }
      return {
        id: technicalSite.ts_uuid,
        label: technicalSiteLabel,
        children: this.createListOfTechnicalLocalsOptions(technicalSite)
      };
    },

    /**
     * Creates a list of Technical Local options.
     * @param {Object} technicalSite
     * @return {Array}
     */
    createListOfTechnicalLocalsOptions(technicalSite) {
      let technicalLocalList = [];
      technicalSite.technicallocals.forEach(technicalLocal => {
        let newTechnicalLocal = this.createTechnicalLocalOption(technicalLocal);
        technicalLocalList.push(newTechnicalLocal);
      });
      return technicalLocalList;
    },

    /**
     * Creates a Technical local object that the treeselect component can read as an option.
     * @param {object} technicalLocal - Original Technical local object
     * @return {object} the adapted object
     */
    createTechnicalLocalOption(technicalLocal) {
      let technicalLocalLabel =
        technicalLocal.tl_technical_site_logical_type +
        " " +
        technicalLocal.tl_id;
      if (technicalLocal.tl_label && technicalLocal.tl_label !== "") {
        technicalLocalLabel += ": " + technicalLocal.tl_label;
      }
      return {
        id: technicalLocal.tl_uuid,
        label: technicalLocalLabel
      };
    },

    ////////////////////////////
    /* Network objects methods*/
    ////////////////////////////

    /**
     * Gets the list of children of a given network object.
     * @param {Object} selectedObject - selected network object.
     * @param {String} initials - initials of the object type
     * @param {String} technicalSiteType
     * @returns {Array} a normalized list of objects
     */
    getObjectChildren(selectedObject, initials, technicalSiteType = undefined) {
      if (initials === "ts" && technicalSiteType !== undefined) {
        if (technicalSiteType === "CO") {
          this.getWorkspaceCentralOfficesOptions(selectedObject.children);
        } else {
          this.getWorkspacePrimaryNodesOptions(selectedObject.children);
        }
      } else {
        return this.optionListNormalizer(selectedObject.children, initials);
      }
    },

    /**
     * Gets all the objects of a given type that are inside the network.
     * @param {Array} networkObjects - list of network objects
     * @param {String} initials - initials of the object type
     * @returns {Array} a normalized list of objects
     */
    getAllObjectsOfTypeInNetwork(networkObjects, initials) {
      let objectsOfType = [];
      if (this.objectIsOfType(networkObjects[0], initials)) {
        // Concatenates all the arrays with objects of the given type
        networkObjects.forEach(object => {
          objectsOfType.concat(object.children);
        });
        return this.optionListNormalizer(objectsOfType, initials);
      } else {
        // Checks if there are objects of the given type in the next level
        this.getAllObjectsOfTypeInNetwork(networkObjects[0].children, initials);
      }
    },

    /**
     * Checks if a given object is of a certain type.
     * @param {Object} object - object to test
     * @param {String} initials - initials of the object type
     * @returns {boolean} true if the object is of the given type, false otherwise.
     */
    objectIsOfType(object, initials) {
      return Object.keys(object).includes(initials);
    },

    ///////////////////////////
    /*List of options methods*/
    ///////////////////////////

    /**
     * Normalizes a list of items to be usable by the treeselect component.
     * @param {Array} originalList - list to be normalized
     * @param {String} initials - initials of the object type
     * @return {Array}
     */
    optionListNormalizer(originalList, initials) {
      let finalOptionsList = [];
      originalList.forEach(object => {
        let label = this.generateObjectLabel(initials, object);

        finalOptionsList.push({
          id: object[initials + "_uuid"],
          label: label,
          geom: object.geom
        });
      });
      return finalOptionsList;
    },

    includeInAllOption(options) {
      return [{ id: "all", label: "All", children: options }];
    },

    /**
     * Generate the label to show in the list of options.
     * @param {String} initials - initials of the object type
     * @param {Object} object - network object being normalized
     * @return {String}
     */
    generateObjectLabel(initials, object) {
      let label = object[initials + "_id"];
      if (object[initials + "_label"]) {
        label += ": " + object[initials + "_label"];
      }
      return label;
    },

    userHasPermissionToList(objectType) {
      if (objectType === "technical_local") {
        return (
          this.userHasFibercloudPermission("technical_site_list") &&
          this.userHasFibercloudPermission("technical_local_list")
        );
      } else {
        return this.userHasFibercloudPermission(objectType, "list");
      }
    },

    /////////////////
    /*Other methods*/
    /////////////////

    resetReportParameterValues(reportParameters, startingIndex) {
      let nrOfParameters = reportParameters.length;
      for (let i = startingIndex; i < nrOfParameters; i++) {
        if (Array.isArray(reportParameters[i].value)) {
          reportParameters[i].value = [];
        } else {
          reportParameters[i].value = undefined;
        }
      }
    },

    fillArrayOfObjectsToSend(values, objectType) {
      let arrayOfObjects = [];
      values.forEach(value => {
        arrayOfObjects.push({ uuid: value, object_type: objectType });
      });
      return arrayOfObjects;
    }
  }
};



// WEBPACK FOOTER //
// ./src/mixins/homeReportPageMixin.js